import { Specialty } from '@uplift/types';

// Keys mirror l10n.onboarding.reason.categories — this module stays copy-free
// and only decides which key a specialty belongs to; reason.tsx resolves the
// key to display text via l10n.
export enum ReasonCategoryKey {
  moodAndEmotions = 'moodAndEmotions',
  focusAndBehaviors = 'focusAndBehaviors',
  traumaAndFears = 'traumaAndFears',
  relationshipsAndIdentity = 'relationshipsAndIdentity',
  lifeChangesAndHealth = 'lifeChangesAndHealth',
  ongoingConditions = 'ongoingConditions',
  other = 'other',
}

type ReasonCategoryMatcher = {
  key: ReasonCategoryKey;
  isMatch: (specialtyName: string) => boolean;
};

// Category assignments are sourced from the MVP-8850 Figma spec
// (5OCfvz2LHf2p9NJUwN8VIs, node 5803-18). Matching is keyword-based rather
// than an exact name lookup because the live specialty catalog is owned by
// the backend and can rename or add entries independently of this list.
const REASON_CATEGORY_MATCHERS: ReasonCategoryMatcher[] = [
  {
    key: ReasonCategoryKey.moodAndEmotions,
    isMatch: name => /\banxiety\b/i.test(name) || /\bdepression\b/i.test(name) || /\banger\b/i.test(name)
  },
  {
    key: ReasonCategoryKey.focusAndBehaviors,
    isMatch: name => /\bAD(D|HD)\b/i.test(name) || /\bocd\b/i.test(name) || /obsessive/i.test(name) || /addiction/i.test(name) || /substance/i.test(name) || /eating disorder/i.test(name)
  },
  {
    key: ReasonCategoryKey.traumaAndFears,
    isMatch: name => /trauma/i.test(name) || /\bptsd\b/i.test(name) || /post.?traumatic/i.test(name) || /phobia/i.test(name)
  },
  {
    key: ReasonCategoryKey.relationshipsAndIdentity,
    isMatch: name => /family relationship/i.test(name) || /romantic relationship/i.test(name) || /lgbtq/i.test(name)
  },
  {
    key: ReasonCategoryKey.lifeChangesAndHealth,
    isMatch: name => /career/i.test(name) || /grief/i.test(name) || /\bloss\b/i.test(name) || /chronic (pain|illness)/i.test(name) || /prenatal/i.test(name) || /postpartum/i.test(name)
  },
  {
    key: ReasonCategoryKey.ongoingConditions,
    isMatch: name => /bipolar/i.test(name) || /psychotic/i.test(name) || /schizophrenia/i.test(name)
  }
];

export type ReasonCategoryGroup = {
  key: ReasonCategoryKey;
  reasons: Specialty[];
};

export const categorizeReasons = (reasons: Specialty[]): ReasonCategoryGroup[] => {
  const categorized = new Set<string>();

  const groups = REASON_CATEGORY_MATCHERS
    .map(category => {
      const matches = reasons.filter(reason => !categorized.has(reason.id) && category.isMatch(reason.name));

      matches.forEach(reason => categorized.add(reason.id));
      return { key: category.key, reasons: matches };
    })
    .filter(group => group.reasons.length > 0);

  const uncategorized = reasons.filter(reason => !categorized.has(reason.id));

  if (uncategorized.length > 0) {
    groups.push({ key: ReasonCategoryKey.other, reasons: uncategorized });
  }

  return groups;
};

const STIMULANT_TRIGGER = (name: string) => /\bAD(D|HD)\b/i.test(name);
const BENZODIAZEPINE_TRIGGER = (name: string) => /\banxiety\b/i.test(name) || /phobia/i.test(name);

export type DisclaimerVariant = 'stimulant' | 'benzodiazepine';

export const getDisclaimerVariants = (selectedReasons: Specialty[]): DisclaimerVariant[] => {
  const variants: DisclaimerVariant[] = [];

  if (selectedReasons.some(reason => STIMULANT_TRIGGER(reason.name))) {
    variants.push('stimulant');
  }

  if (selectedReasons.some(reason => BENZODIAZEPINE_TRIGGER(reason.name))) {
    variants.push('benzodiazepine');
  }

  return variants;
};
